import { produce } from "immer";
import debounce from "lodash.debounce";

import {
    FilterSourceInfoType,
    getAllPathsFromExpression,
    MultiLineFilter,
    MultiLineFilters,
} from "../../../Controls/MultiControls/MultiLine/MultiLineFilter";
import { MultiLineSort } from "../../../Controls/MultiControls/MultiLine/MultiLineTypes";
import {
    AbsoluteModelPath,
    AbsoluteResolvedModelPath,
    convertToAbsoluteModelPath,
    convertToLegacyPath,
} from "../../../../../Common/ModelPath/ModelPath";
import { FilterExpression } from "../../../Helpers/FilterExpressions/FilterExpression";
import { uuid } from "../../../../../Common/UUID";
import { safeGetFilter, setFilter } from "../../../Controls/MultiControls/MultiLine/Store/FiltersSessionHelpers";
import { ILazyLoadingManager, LazyLoadingInstanceState } from "../LazyLoading/LazyLoadingManager";
import { AbsoluteModelFieldPath } from "../../../../../Common/ModelPath/AbsoluteModelFieldPath";
import { MatchKind } from "../../../../../Common/ModelPath/PathMatcher";
import { createAutoCancellableAsyncQueue } from "../Calculator2/Calculator2Compatibility";
import { Set2 } from "../../../../../Common/ModelPath/Set/Set2";
import { DataDeclarationHelper } from "../DataDeclaration/DataDeclarationHelper";
import { resolveFilterDataPath } from "../../../Helpers/FilteringHelpers";
import { arrayIsNotEmpty, runAsyncAction } from "../../../../../Common/TypingUtils";
import { getInstancesAfterAdd, getInstancesAfterRemove } from "../../../Helpers/StateAccessors/MultilineHelpers";
import { getCandyHub } from "../../../Forms/Sentry/InitializeSentry";
import {
    DataFieldRow,
    DataFieldValue,
} from "../../../../../Common/CandyApiClient/Types/QueryCollecton/InnerQueryResponse";
import { RelativeModelFieldPath } from "../../../../../Common/ModelPath/RelativeModelFieldPath";

import { AggregationType } from "./IAggregationQueryStrategy";
import { ISimpleModelAccessor } from "./FilteringManager";
import { IFilteringStrategy } from "./IFilteringStrategy";

export type Unsubscriber = () => void;

export interface IMultilineManagerClient {
    setFilter(filter: MultiLineFilter): void;
    setSorting(sorting: MultiLineSort | undefined): void;
    setCurrentPage(currentPage: number): void;
    removeFilter(filterSourceId: string): void;
    removeAllFilters(): void;
    requestFiltering(): void;
    waitForPendingOperations(): Promise<void>;
    getCurrentState(): IMultilineState;
}

export interface IMultilineState {
    filters: MultiLineFilters;
    sorting: MultiLineSort | undefined;
    currentPage: number;
    loading: boolean;
    useServerFilter: boolean;
    instancesPerPage: number;
    isMultilineConnected: boolean;
    displayedInstances: readonly string[];
    filteringResultVersion: number;
}

export interface IMultilineStateWithInnerParts extends IMultilineState {
    requiredFilteringOnInit?: boolean;
    storeFiltersWithinSession: boolean;
    requiredTrackingPaths: Set2<AbsoluteModelFieldPath>;
    allTrackingPaths: Set2<AbsoluteModelFieldPath>;
}

const DefaultServerSideFilterDebounceTimeout = 500;
const DefaultClientSideFilterDebounceTimeout = 400;
const ShortFilterDebounceTimeout = 20;

export class MultilineManager implements IMultilineManagerClient {
    private readonly subscriptions = new Map<string, (multilineState: IMultilineState) => void>();
    private readonly lazyLoadingManager: ILazyLoadingManager;
    private readonly sendToServerAction: () => Promise<void>;
    private readonly dataDeclarationHelper: DataDeclarationHelper;
    private readonly clientSideFilteringStrategy: IFilteringStrategy;
    private readonly serverSideFilteringStrategy: IFilteringStrategy;
    private multilineState: IMultilineStateWithInnerParts;
    private handleFilterChangeDebounced: ((resetToFirstPage?: boolean) => void | undefined) | undefined;
    private readonly modelAccessor: ISimpleModelAccessor;
    public readonly bindingPath: AbsoluteResolvedModelPath;
    public readonly filteringUniqueId: string;
    public readonly queue = createAutoCancellableAsyncQueue();

    public constructor(
        dataDeclarationHelper: DataDeclarationHelper,
        lazyLoadingManager: ILazyLoadingManager,
        sendToServerAction: () => Promise<void>,
        clientSideFilteringStrategy: IFilteringStrategy,
        serverSideFilteringStrategy: IFilteringStrategy,
        modelAccessor: ISimpleModelAccessor,
        bindingPath: AbsoluteResolvedModelPath,
        filteringUniqueId: string
    ) {
        this.modelAccessor = modelAccessor;
        this.dataDeclarationHelper = dataDeclarationHelper;
        this.lazyLoadingManager = lazyLoadingManager;
        this.sendToServerAction = sendToServerAction;
        this.clientSideFilteringStrategy = clientSideFilteringStrategy;
        this.serverSideFilteringStrategy = serverSideFilteringStrategy;
        this.bindingPath = bindingPath;
        this.filteringUniqueId = filteringUniqueId;
        this.multilineState = this.getDefaultState();
    }

    public subscribeMultiline(
        instancesPerPage: number,
        storeFiltersWithinSession: boolean,
        useShortDebounce: boolean,
        defaultFilterExpression: FilterExpression | undefined,
        onStateUpdated: (multilineState: IMultilineState) => void
    ): Unsubscriber {
        const unsubscriber = this.addSubscription(onStateUpdated);
        const savedFilter = arrayIsNotEmpty(this.multilineState.filters)
            ? this.multilineState.filters
            : storeFiltersWithinSession
              ? (safeGetFilter(this.filteringUniqueId, this.bindingPath.toLegacyPath())?.filter(
                    x => x.filterSourceInfo.type !== FilterSourceInfoType.DefaultFilter
                ) ?? [])
              : [];

        const defaultFilter: MultiLineFilter | undefined =
            defaultFilterExpression != undefined
                ? {
                      expression: defaultFilterExpression,
                      filterSourceId: "defaultFilterSelect",
                      filterSourceInfo: {
                          type: FilterSourceInfoType.DefaultFilter,
                      },
                  }
                : undefined;
        const useServerFilters = this.dataDeclarationHelper.getEntry(this.bindingPath)?.useServerFilters ?? false;

        this.handleFilterChangeDebounced = debounce(
            (resetToFirstPage?: boolean) =>
                this.queue.enqueue(async signal => await this.handleFilterChange(resetToFirstPage ?? true, signal)),
            useShortDebounce
                ? ShortFilterDebounceTimeout
                : useServerFilters
                  ? DefaultServerSideFilterDebounceTimeout
                  : DefaultClientSideFilterDebounceTimeout
        );

        const initialFilters: MultiLineFilters = this.mergeFilters(savedFilter, defaultFilter);
        const canShowChildrenAsDisplayedInstances =
            !useServerFilters && !arrayIsNotEmpty(initialFilters) && this.multilineState.sorting == undefined;
        const requiredInitialFiltering =
            this.multilineState.requiredFilteringOnInit == true ||
            (this.multilineState.requiredFilteringOnInit == undefined && !canShowChildrenAsDisplayedInstances);
        const initialDisplayedInstances = !canShowChildrenAsDisplayedInstances
            ? this.multilineState.displayedInstances
            : (this.modelAccessor.getChildren(this.bindingPath) ?? []);

        this.updateLoadingStateIfRequired(this.multilineState.currentPage, initialDisplayedInstances);
        this.runLoadingInstancesIfNeededWithStateUpdate(this.multilineState.currentPage, initialDisplayedInstances);

        this.updateState(state => {
            state.filters = initialFilters;
            state.useServerFilter = useServerFilters;
            state.storeFiltersWithinSession = storeFiltersWithinSession;
            state.isMultilineConnected = true;
            state.instancesPerPage = instancesPerPage;
            state.displayedInstances = initialDisplayedInstances;
        });

        this.updateSubscriptionDependencies();

        if (requiredInitialFiltering) {
            this.queue.enqueue(signal => this.handleFilterChange(false, signal), true);
        }
        return () => {
            this.updateState(state => {
                state.isMultilineConnected = false;
            });
            unsubscriber();
            this.queue.abortCurrent();
        };
    }

    public subscribeClient(onStateUpdated: (multilineState: IMultilineState) => void): Unsubscriber {
        onStateUpdated(this.multilineState);
        return this.addSubscription(onStateUpdated);
    }

    public setFilter(filter: MultiLineFilter) {
        this.updateState(state => {
            state.filters = this.mergeFilters(state.filters, filter);
        });
        this.storeFiltersIfRequired();
        this.updateSubscriptionDependencies();
        this.requestFiltering();
    }

    public setSorting(sorting: MultiLineSort | undefined) {
        this.updateState(state => {
            state.sorting = sorting;
        });
        this.requestFiltering();
    }

    public setCurrentPage(page: number) {
        this.queue.enqueue(async () => {
            if (!this.multilineState.isMultilineConnected) {
                return;
            }
            const displayedInstances = this.multilineState.displayedInstances;
            const pageCount: number = Math.ceil(displayedInstances.length / this.multilineState.instancesPerPage);
            const currentPage: number = Math.max(1, Math.min(page, pageCount));

            this.updateLoadingStateIfRequired(currentPage, displayedInstances);
            this.updateState(state => {
                state.currentPage = currentPage;
            });
            this.runLoadingInstancesIfNeededWithStateUpdate(currentPage, displayedInstances);
        }, true);
    }

    public removeFilter(filterSourceId: string) {
        this.updateState(state => {
            const indexToRemove = state.filters.findIndex(x => x.filterSourceId === filterSourceId);
            if (indexToRemove >= 0) {
                state.filters = [...state.filters.slice(0, indexToRemove), ...state.filters.slice(indexToRemove + 1)];
            }
        });
        this.storeFiltersIfRequired();
        this.updateSubscriptionDependencies();
        this.requestFiltering();
    }

    public removeAllFilters() {
        this.updateState(state => {
            state.filters = [];
        });
        this.storeFiltersIfRequired();
        this.updateSubscriptionDependencies();
        this.requestFiltering();
    }

    public requestFiltering(resetToFirstPage?: boolean) {
        if (!this.multilineState.requiredFilteringOnInit) {
            this.updateState(state => {
                state.requiredFilteringOnInit = true;
            });
        }
        if (this.multilineState.isMultilineConnected) {
            this.handleFilterChangeDebounced?.(resetToFirstPage);
        }
    }

    public waitForPendingOperations(): Promise<void> {
        return this.queue.enqueueWithPromise(async _signal => Promise.resolve(), true);
    }

    public notifyWithChanges(changedPathFields: Set2<AbsoluteModelFieldPath>) {
        const hasFilterOrSorting =
            arrayIsNotEmpty(this.multilineState.filters) || this.multilineState.sorting != undefined;

        const trackingPaths = this.multilineState.isMultilineConnected
            ? this.multilineState.requiredTrackingPaths
            : this.multilineState.allTrackingPaths;

        for (const changedPathField of changedPathFields.values()) {
            for (const filterPath of trackingPaths.values()) {
                if (
                    filterPath.field === changedPathField.field &&
                    filterPath.path
                        .matchFor(changedPathField.path)
                        .every(x => x.kind === MatchKind.MatchedPath || x.kind === MatchKind.MatchedMultiplicity)
                ) {
                    this.requestFiltering(false);
                    return;
                }
            }

            if (changedPathField.field == "children" && this.bindingPath.isEquals(changedPathField.path)) {
                const hasDefaultFilter = this.multilineState.filters.some(
                    x => x.filterSourceInfo.type == FilterSourceInfoType.DefaultFilter
                );
                if (hasDefaultFilter || !hasFilterOrSorting) {
                    this.queue.enqueue(signal => this.handleFilterChange(false, signal), true);
                    return;
                }
            }
        }
    }

    public async requestInstancesWithExtraFilter(filterExpression: FilterExpression) {
        const filterStrategy = this.getFilteringStrategy(this.multilineState);
        const filterSource: MultiLineFilter = {
            expression: filterExpression,
            filterSourceId: "NoSource",
            filterSourceInfo: { type: FilterSourceInfoType.DefaultFilter },
        };
        const sourceFilters = this.multilineState.filters;
        const resultFilter = [...sourceFilters, filterSource];
        const filteredInstances = await filterStrategy.performFilteringAndSorting(
            convertToLegacyPath(this.bindingPath),
            resultFilter.map(x => x.expression),
            this.multilineState.sorting
        );
        return filteredInstances;
    }

    public async requestItemsWithExtraFilter(
        filterExpression: FilterExpression,
        requestPaths: RelativeModelFieldPath[]
    ): Promise<DataFieldRow[]> {
        const filterStrategy = this.getFilteringStrategy(this.multilineState);
        const filterSource: MultiLineFilter = {
            expression: filterExpression,
            filterSourceId: "NoSource",
            filterSourceInfo: { type: FilterSourceInfoType.DefaultFilter },
        };
        const sourceFilters = this.multilineState.filters;
        const resultFilter = [...sourceFilters, filterSource];
        await this.sendDataToServerIfRequired(this.multilineState);
        const filteredInstances = await filterStrategy.performFilteringAndSortingItems(
            convertToLegacyPath(this.bindingPath),
            requestPaths,
            resultFilter.map(x => x.expression),
            this.multilineState.sorting
        );
        return filteredInstances;
    }

    public async getAggregationValue(
        pathToValue: RelativeModelFieldPath,
        aggregationType: AggregationType
    ): Promise<DataFieldValue> {
        const filterStrategy = this.getFilteringStrategy(this.multilineState);
        await this.sendDataToServerIfRequired(this.multilineState);
        const result = await filterStrategy.getAggregationValue(
            convertToLegacyPath(this.bindingPath),
            pathToValue,
            aggregationType
        );
        return result;
    }

    public getCurrentState(): IMultilineState {
        return this.multilineState;
    }

    public handleAddInstances(
        instances: readonly string[],
        insertAfterInstance?: string,
        insertBeforeInstance?: string
    ) {
        const displayedInstances = this.multilineState.displayedInstances;
        const instanceIndex = insertAfterInstance
            ? displayedInstances.indexOf(insertAfterInstance) + 1
            : insertBeforeInstance
              ? displayedInstances.indexOf(insertBeforeInstance)
              : displayedInstances.length;

        const hasDefaultFilter = this.multilineState.filters.some(
            x => x.filterSourceInfo.type === FilterSourceInfoType.DefaultFilter
        );
        if (hasDefaultFilter) {
            return;
        }

        const hasFilterOrSorting =
            arrayIsNotEmpty(this.multilineState.filters) || this.multilineState.sorting != undefined;

        const newInstances = hasFilterOrSorting
            ? getInstancesAfterAdd(displayedInstances, instances[0], instanceIndex)
            : (this.modelAccessor.getChildren(this.bindingPath) ?? []);

        const instanceCount = newInstances.length;
        const instancesPerPage = this.multilineState.instancesPerPage;

        const newCurrentPage: number = insertAfterInstance
            ? Math.ceil((Number(instanceIndex) + 1) / instancesPerPage)
            : insertBeforeInstance
              ? Math.ceil(Number(instanceIndex) / instancesPerPage)
              : Math.ceil(instanceCount / instancesPerPage);

        this.updateState(state => {
            state.displayedInstances = newInstances;
        });

        if (newCurrentPage != this.multilineState.currentPage) {
            this.setCurrentPage(newCurrentPage);
        }
    }

    public handleRemoveInstances(instances: readonly string[]) {
        const displayedInstances = this.multilineState.displayedInstances;

        const hasFilterOrSorting =
            arrayIsNotEmpty(this.multilineState.filters) || this.multilineState.sorting != undefined;

        const newInstances = hasFilterOrSorting
            ? getInstancesAfterRemove(displayedInstances, instances)
            : (this.modelAccessor.getChildren(this.bindingPath) ?? []);

        const instanceCount = newInstances.length;
        const currentPage = this.multilineState.currentPage;
        const instancesPerPage = this.multilineState.instancesPerPage;
        const isLastInstanceOnCurrentPage: boolean = instanceCount - (currentPage - 1) * instancesPerPage <= 0;
        const newCurrentPage: number = isLastInstanceOnCurrentPage ? Math.max(1, currentPage - 1) : currentPage;

        this.updateState(state => {
            state.displayedInstances = newInstances;
        });
        this.setCurrentPage(newCurrentPage);
    }

    private mergeFilters(sourceFilters: MultiLineFilters, filter: MultiLineFilter | undefined) {
        if (filter == undefined) {
            return sourceFilters;
        }
        const existingIndex = sourceFilters.findIndex(x => x.filterSourceId === filter.filterSourceId);
        if (existingIndex < 0) {
            return [...sourceFilters, filter];
        } else {
            return [...sourceFilters.slice(0, existingIndex), filter, ...sourceFilters.slice(existingIndex + 1)];
        }
    }

    private runLoadingInstancesIfNeededWithStateUpdate(currentPage: number, displayedInstances: readonly string[]) {
        const requiredInstances = this.getPreloadedInstances(
            currentPage,
            this.multilineState.instancesPerPage,
            displayedInstances
        );
        runAsyncAction(async () => {
            try {
                await this.loadInstancesIfNeeded(this.bindingPath, requiredInstances);
            } finally {
                this.queue.enqueue(async () => {
                    if (this.multilineState.currentPage === currentPage) {
                        this.updateLoadingStateIfRequired(
                            this.multilineState.currentPage,
                            this.multilineState.displayedInstances
                        );
                    }
                });
            }
        });
    }

    private async handleFilterChange(resetToFirstPage: boolean = true, _abortSignal?: AbortSignal) {
        if (!this.multilineState.isMultilineConnected) {
            return;
        }
        try {
            const filters = this.multilineState.filters;
            const sorting = this.multilineState.sorting;
            await this.sendDataToServerIfRequired(this.multilineState);

            const filterStrategy = this.getFilteringStrategy(this.multilineState);
            const filteringRequestRequired = filters.length > 0 || sorting != undefined;
            if (filteringRequestRequired) {
                this.updateState(state => {
                    state.loading = true;
                });
            }
            const displayedInstances = filteringRequestRequired
                ? await filterStrategy.performFilteringAndSorting(
                      convertToLegacyPath(this.bindingPath),
                      filters.map(x => x.expression),
                      this.multilineState.sorting
                  )
                : (this.modelAccessor.getChildren(this.bindingPath) ?? []);
            const currentPage = resetToFirstPage
                ? 1
                : this.calculateCurrentPage(
                      this.multilineState.currentPage,
                      displayedInstances.length,
                      this.multilineState.instancesPerPage
                  );
            this.updateLoadingStateIfRequired(currentPage, displayedInstances);
            await this.loadInstancesIfNeeded(
                convertToAbsoluteModelPath(this.bindingPath),
                this.getPreloadedInstances(currentPage, this.multilineState.instancesPerPage, displayedInstances)
            );
            this.updateState(state => {
                state.currentPage = currentPage;
                state.displayedInstances = displayedInstances;
                state.filteringResultVersion += 1;
                state.requiredFilteringOnInit = false;
            });
        } catch (e) {
            getCandyHub().captureException(e);
        } finally {
            this.updateLoadingStateIfRequired(this.multilineState.currentPage, this.multilineState.displayedInstances);
        }
    }

    private updateLoadingStateIfRequired(currentPage: number, displayedInstances: readonly string[]) {
        const preloadedInstances = this.getPreloadedInstances(
            currentPage,
            this.multilineState.instancesPerPage,
            displayedInstances,
            0
        );
        const requiredInstances = this.getNotLoadedInstances(this.bindingPath, preloadedInstances);
        const loadingRequired = requiredInstances.length > 0;
        if (this.multilineState.loading !== loadingRequired) {
            this.updateState(state => {
                state.loading = loadingRequired;
            });
        }
    }

    private updateSubscriptionDependencies() {
        const requiredFilterChecker = (filter: MultiLineFilter) =>
            filter.filterSourceInfo.type == FilterSourceInfoType.DefaultFilter;

        const filters = this.multilineState.filters;

        const requiredTrackingPaths = this.multilineState.requiredTrackingPaths;
        const allTrackingPaths = this.multilineState.allTrackingPaths;
        requiredTrackingPaths.clear();
        allTrackingPaths.clear();
        const pathInfos = filters
            .flatMap(x => {
                const isRequired = requiredFilterChecker(x);
                return getAllPathsFromExpression(x.expression).map(path => ({
                    filterPath: path,
                    isRequired: isRequired,
                }));
            })
            .map(x => ({
                pathWithField: resolveFilterDataPath(x.filterPath, this.bindingPath),
                isRequired: x.isRequired,
            }));

        for (const pathInfo of pathInfos) {
            if (pathInfo.isRequired) {
                requiredTrackingPaths.add(pathInfo.pathWithField);
            }
            allTrackingPaths.add(pathInfo.pathWithField);
        }
    }

    private getFilteringStrategy(multilineState: IMultilineStateWithInnerParts): IFilteringStrategy {
        const isServerFilter = multilineState.useServerFilter ?? false;
        return isServerFilter ? this.serverSideFilteringStrategy : this.clientSideFilteringStrategy;
    }

    private async sendDataToServerIfRequired(multilineState: IMultilineStateWithInnerParts): Promise<void> {
        const isServerFilter = multilineState.useServerFilter ?? false;
        if (isServerFilter) {
            await this.sendToServerAction();
        }
    }

    private getNotLoadedInstances(bindingPath: AbsoluteModelPath, instances: string[]) {
        const lazyLoadingManager = this.lazyLoadingManager;
        const isLazyLoadingEnabled = lazyLoadingManager.isLazyLoadingEnabled();
        const requiredInstances = isLazyLoadingEnabled
            ? instances.filter(
                  x =>
                      lazyLoadingManager.getInstanceLazyLoadingState(bindingPath, x) ===
                          LazyLoadingInstanceState.OnlyInitialData ||
                      lazyLoadingManager.getInstanceLazyLoadingState(bindingPath, x) ===
                          LazyLoadingInstanceState.Pending
              )
            : [];
        return requiredInstances;
    }

    private async loadInstancesIfNeeded(bindingPath: AbsoluteModelPath, instances: string[]) {
        const lazyLoadingManager = this.lazyLoadingManager;
        const requiredInstances = this.getNotLoadedInstances(bindingPath, instances);
        if (requiredInstances.length > 0) {
            await lazyLoadingManager.requestInstancesLoad(bindingPath, ...requiredInstances);
        }
    }

    private getPreloadedInstances(
        currentPage: number,
        instancesPerPage: number,
        filteredInstances: readonly string[],
        preloadedPagesCount: number = 4
    ): string[] {
        const result = filteredInstances.slice(
            (currentPage - 1) * instancesPerPage,
            (currentPage + preloadedPagesCount) * instancesPerPage
        );
        return result;
    }

    private calculateCurrentPage(pageNumber: number, displayedInstancesCount: number, instancesPerPage: number) {
        const pageCount: number = Math.ceil(displayedInstancesCount / instancesPerPage);
        const currentPage: number = Math.max(1, Math.min(pageNumber, pageCount));
        return currentPage;
    }

    private addSubscription(onStateUpdated: (multilineState: IMultilineState) => void): Unsubscriber {
        const key = uuid();
        this.subscriptions.set(key, onStateUpdated);
        return () => {
            this.subscriptions.delete(key);
        };
    }

    private updateState(updater: (currentState: IMultilineStateWithInnerParts) => void) {
        const prevState = this.multilineState;
        this.multilineState = produce(prevState, updater);
        if (prevState != this.multilineState) {
            for (const subscription of this.subscriptions.values()) {
                subscription(this.multilineState);
            }
        }
    }

    private getDefaultState(): IMultilineStateWithInnerParts {
        return {
            currentPage: 1,
            displayedInstances: [],
            filters: [],
            sorting: undefined,
            isMultilineConnected: false,
            instancesPerPage: 10,
            loading: false,
            useServerFilter: false,
            storeFiltersWithinSession: false,
            requiredTrackingPaths: new Set2<AbsoluteModelFieldPath>(),
            allTrackingPaths: new Set2<AbsoluteModelFieldPath>(),
            filteringResultVersion: 0,
        };
    }

    private storeFiltersIfRequired() {
        if (this.multilineState.storeFiltersWithinSession) {
            setFilter(
                this.filteringUniqueId,
                this.bindingPath.toLegacyPath(),
                JSON.stringify(
                    this.multilineState.filters.filter(
                        x => x.filterSourceInfo.type !== FilterSourceInfoType.DefaultFilter
                    )
                )
            );
        }
    }
}
